<template>
  <div
    class="training-program-sub-item training-sub-item overflow-visible"
    :style="{
      '--exercise-color': exerciseTypeColor
    }"
  >
    <div class="flex justify-between items-center training-program-sub-item__title border-b border-dark-60">
      <div class="whitespace-nowrap flex flex-row justify-start items-center">
        <i class="cursor-move text-24 ml-20 align-middle inline-block iconfont iconicon_jichutubiao_yidong" />
        <template v-if="readonly">
          <div class="exercise-type-name ow-h1 pl-12">
            {{ $t(modelValue.name) }}
          </div>
        </template>
        <template v-else>
          <template v-if="isStrength">
            <SelectStrengthExercise
              :disabled="readonly"
              :default-exercise-name="$t(modelValue.originExercise.name)"
              :model-value="modelValue.originId"
              :team-id="teamId"
              :user-id="user.userId"
              class="strength-exercise-select"
              @change="changeStrengthExercise"
            />
            <StrengthExerciseDetail
              v-if="showStrengthDetailButton"
              :active-strength-exercise="activeStrengthExercise"
            />
          </template>
          <Select
            v-else
            class="exercise-type bg-transparent align-middle w-auto relative"
            :class="['exercise-type-' + containerId]"
            :placeholder="$t('R4015')"
            :model-value="modelValue.exerciseTypeName"
            :trigger-props="universalTriggerProps4select"
            @change="changeExerciseType"
          >
            <Option
              v-for="item in exerciseTypeOptions"
              :key="item.key"
              :value="item.key"
            >
              {{ item.nameText }}
            </Option>
          </Select>
        </template>
      </div>
      <div
        v-if="!readonly"
        class="exercise-operate-btns"
      >
        <Tooltip
          class="item"
          effect="dark"
          :content="$t('R3008')"
          placement="top"
        >
          <i
            class="mr-40 cursor-pointer iconfont iconicon_jichutubiao_shanchu1 operate-icon"
            @click="emit('delete')"
          ></i>
        </Tooltip>
        <Tooltip
          class="item"
          effect="dark"
          :content="$t('R3006')"
          placement="top"
        >
          <i
            class="mr-40 cursor-pointer iconfont iconicon_jichutubiao_fuzhi1 operate-icon"
            @click="emit('copy')"
          ></i>
        </Tooltip>
        <Tooltip
          class="item"
          effect="dark"
          :content="$t('R4010')"
          placement="top"
        >
          <i
            class="mr-16 cursor-pointer iconfont iconicon_jichutubiao_tianjiakapian1 operate-icon"
            @click="emit('add')"
          ></i>
        </Tooltip>
      </div>
    </div>
    <div class="training-program-sub-item__content overflow-visible flex flex-row items-center">
      <!-- 目标类型-->
      <span
        v-if="targetOptions.length === 1"
        class="px-[12px]"
      >
        {{ targetOptions[0].label }}
      </span>
      <Select
        v-else
        class="target-type w-auto align-middle relative"
        :class="['target-type-' + containerId]"
        type="text"
        :disabled="readonly"
        :options="targetOptions"
        :value-key="'key'"
        :text-key="'nameText'"
        :trigger-props="universalTriggerProps4select"
        :model-value="modelValue.targetTypeName"
        @update:model-value="(val) => updateModelValueByPath({ path: 'targetTypeName', val })"
      >
        <template #label="{ data }">
          <!-- 游泳移除 负荷选项，但是要支持回显旧数据 -->
          <span> {{ sportType === 3 && data.value === 'load' ? $t('S4491') : data.label }}</span>
        </template>
      </Select>

      <!-- 目标值（时间）-->
      <template v-if="modelValue.targetTypeName === 'time'">
        <TimePicker
          class="duration-picker item-with-suffix border-dark-60"
          :disabled="readonly"
          :allow-clear="false"
          disableConfirm
          :min="exerciseTargetConfig.inputConfig.min"
          :max="exerciseTargetConfig.inputConfig.max"
          :model-value="parseDuration2time(modelValue.targetCount.time.value)"
          @update:model-value="(val) => updateModelValueByPath({ path: 'targetCount.time.value', val: hhmmss2seconds(val) })"
        >
          <template #suffix-icon>
            <span class="color-gray-500">h:m:s</span>
          </template>
        </TimePicker>
      </template>
      <!-- 目标值（次数）-->
      <template v-if="modelValue.targetTypeName === 'count'">
        <InputNumberFixed
          class="target-count w-145 item-with-suffix border border-dark-60 rounded-sm hover:border-primary-500"
          :disabled="readonly"
          :min="exerciseTargetConfig.inputConfig.min"
          :max="exerciseTargetConfig.inputConfig.max"
          :step="exerciseTargetConfig.inputConfig.step"
          :precision="exerciseTargetConfig.inputConfig.precision"
          :model-value="modelValue.targetCount.count.value"
          hide-button
          @update:model-value="(val) => updateModelValueByPath({ path: 'targetCount.count.value', val })"
        >
          <template #suffix>
            <span class="px-10 border-border-1 border-l">{{ modelValue.targetCount.count.value > 1 ? 'Reps' : 'Rep' }}</span>
          </template>
        </InputNumberFixed>
      </template>
      <!-- 目标值（距离）-->
      <template v-if="modelValue.targetTypeName === 'distance'">
        <InputGroup class="border border-dark-60 rounded-sm w-160">
          <!-- 游泳专用，只提供选择，不自由输入，选项根据当前的泳池长度来决定 -->
          <Select
            v-if="sportType === 3 && distanceOptions.length"
            class="w-160"
            :disabled="readonly"
            :model-value="modelValue.targetCount.distance.value"
            @update:model-value="(val) => updateModelValueByPath({ path: 'targetCount.distance.value', val })"
            :options="distanceOptions"
            :format-label="(data) => `${data.labelForDisplay}`"
            :virtual-list-props="{ height: 200 }"
            :trigger-props="universalTriggerProps4select"
          />
          <InputNumberFixed
            v-if="sportType !== 3"
            class="border-border-1 border-r"
            hide-button
            :disabled="readonly"
            :precision="exerciseTargetConfig.inputConfig.precision"
            :min="exerciseTargetConfig.inputConfig.min"
            :max="exerciseTargetConfig.inputConfig.max"
            :step="exerciseTargetConfig.inputConfig.step"
            :model-value="modelValue.targetCount.distance.value"
            @update:model-value="(val) => updateModelValueByPath({ path: 'targetCount.distance.value', val })"
          />
          <span
            v-if="sportType === 3"
            class="unit-append border-l w-80 text-center border-none"
            >{{ poolLengthUnit === 2 ? 'm' : 'yd' }}</span
          >
          <Select
            v-if="sportType !== 3"
            :model-value="modelValue.targetCount.distance.unit"
            class="unit-type unit-type-distance w-80 relative"
            :class="['unit-type-distance-' + containerId]"
            :placeholder="$t('R4015')"
            :disabled="readonly || sportType === 3"
            :trigger-props="universalTriggerProps4select"
            @change="changeTargetDistanceUnit"
            @click.stop
          >
            <Option
              v-for="item in targetUnitOptions"
              :key="item.key"
              :value="item.key"
              >{{ item.nameText }}</Option
            >
          </Select>
        </InputGroup>

        <template v-if="sportType === 3 && modelValue.exerciseTypeName === 'package'">
          <span class="px-[12px]">
            <!--包干时间-->
            {{ $t('S4691') }}
          </span>
          <TimePicker
            class="duration-picker item-with-suffix border-dark-60"
            :disabled="readonly"
            :allow-clear="false"
            disableConfirm
            :min="exerciseTargetConfig.inputConfig.min"
            :max="exerciseTargetConfig.inputConfig.max"
            :model-value="parseDuration2time(modelValue.packageTime)"
            @update:model-value="(val) => updateModelValueByPath({ path: 'packageTime', val: hhmmss2seconds(val) })"
          >
            <template #suffix-icon>
              <span class="color-gray-500">h:m:s</span>
            </template>
          </TimePicker>
        </template>
      </template>
      <!-- 目标值（负荷）-->
      <template v-if="modelValue.targetTypeName === 'load'">
        <div class="border-dark-60 border rounded-sm w-120">
          <InputNumberFixed
            :model-value="modelValue.targetCount.load.value"
            @update:model-value="(val) => updateModelValueByPath({ path: 'targetCount.load.value', val })"
            hide-button
            :disabled="readonly"
            :min="exerciseTargetConfig.inputConfig.min"
            :max="exerciseTargetConfig.inputConfig.max"
            :step="exerciseTargetConfig.inputConfig.step"
            :precision="exerciseTargetConfig.inputConfig.precision"
          >
            <template #suffix>
              <span class="color-gray-500 border-l border-border-1 px-10">TL</span>
            </template>
          </InputNumberFixed>
        </div>
      </template>
      <!-- 累计爬升 -->
      <template v-if="modelValue.targetTypeName === 'cumulativeClimb'">
        <InputGroup class="border border-dark-60 rounded-sm w-160">
          <InputNumberFixed
            :model-value="modelValue.targetCount.cumulativeClimb.value"
            :disabled="readonly"
            :min="exerciseTargetConfig.inputConfig.min"
            :max="exerciseTargetConfig.inputConfig.max"
            :step="exerciseTargetConfig.inputConfig.step"
            :precision="exerciseTargetConfig.inputConfig.precision"
            @update:model-value="(val) => updateModelValueByPath({ path: 'targetCount.cumulativeClimb.value', val })"
            hide-button
            class="border-dark-60 border-r"
          >
          </InputNumberFixed>
          <Select
            :model-value="modelValue.targetCount.cumulativeClimb.unit"
            :disabled="readonly"
            @change="changeTargetDistanceUnit"
            class="w-80"
          >
            <Option
              v-for="(item, index) in targetUnitOptions"
              :key="index"
              :value="item.key"
              :label="item.nameText"
            >
            </Option>
          </Select>
        </InputGroup>
      </template>
      <!-- 目标值（心率恢复）-->
      <template v-if="modelValue.targetTypeName === 'heartRateRecovery'">
        <InputNumberFixed
          :model-value="modelValue.targetCount.heartRateRecovery.value"
          @update:model-value="(val) => updateModelValueByPath({ path: 'targetCount.heartRateRecovery.value', val })"
          class="w-140 border-dark-60 border rounded-sm"
          :disabled="readonly"
          :min="exerciseTargetConfig.inputConfig.min"
          :max="exerciseTargetConfig.inputConfig.max"
          :step="exerciseTargetConfig.inputConfig.step"
          :precision="exerciseTargetConfig.inputConfig.precision"
        >
          <template #suffix>
            <span class="text-text-3 px-10 border-l border-dark-60">bpm</span>
          </template>
        </InputNumberFixed>
      </template>
      <!-- 目标值（线路）-->
      <template v-if="modelValue.targetTypeName === 'routes'">
        <InputNumberFixed
          :model-value="modelValue.targetCount.routes.value"
          @update:model-value="(val) => updateModelValueByPath({ path: 'targetCount.routes.value', val })"
          class="w-80 border-dark-60 border rounded-sm"
          :disabled="readonly"
          :min="exerciseTargetConfig.inputConfig.min"
          :max="exerciseTargetConfig.inputConfig.max"
          :step="exerciseTargetConfig.inputConfig.step"
          :precision="exerciseTargetConfig.inputConfig.precision"
        />
      </template>
      <!-- 强度-->

      <template v-if="fastIntensityTypeOptions.length && modelValue.targetTypeName !== 'heartRateRecovery'">
        <!-- 快捷强度类型-->
        <div class="line-placeholder inline-block mx-24 align-middle"></div>
        <Select
          v-if="!isStrength && !['gradeSystem', 'swimmingStyle'].includes(modelValue.intensityTypeName)"
          v-model="modelValue.intensityTypeName"
          class="mr-10 w-auto align-middle program-intensity-type-select relative"
          :class="['program-intensity-type-select-' + containerId]"
          :popper-class="['program-intensity-type-select-' + containerId]"
          :disabled="readonly"
          :trigger-props="universalTriggerProps4select"
          @change="changeIntensityType"
        >
          <Option
            v-for="item in fastIntensityTypeOptions"
            :key="item.key"
            :value="item.key"
            >{{ item.nameText }}</Option
          >
        </Select>
        <!-- 强度值--重量区间-->
        <template v-if="modelValue.intensityTypeName === 'weight'">
          <Select
            :model-value="modelValue.intensityCount[modelValue.intensityTypeName].type"
            @update:model-value="(val) => updateModelValueByPath({ path: `intensityCount.${modelValue.intensityTypeName}.type`, val })"
            class="pl-0 intensity-count w-auto relative"
            :class="['intensity-count-' + containerId]"
            :disabled="readonly"
            :trigger-props="universalTriggerProps4select"
          >
            <Option
              v-for="item in exerciseIntensityConfig.typeConfig.options"
              :key="item.type"
              :value="item.type"
              >{{ item.nameText }}</Option
            >
          </Select>
          <InputNumber
            v-if="isCustomZoneType"
            :model-value="modelValue.intensityCount[modelValue.intensityTypeName].value"
            @update:model-value="(val) => updateModelValueByPath({ path: `intensityCount.${modelValue.intensityTypeName}.value`, val })"
            class="item-with-suffix weight-count w-auto pr-0 input-with-suffix border border-dark-60 hover:border-primary-500"
            :disabled="readonly"
            :precision="exerciseIntensityConfig.inputConfig.precision"
            :min="exerciseIntensityConfig.inputConfig.min"
            :max="exerciseIntensityConfig.inputConfig.max"
            :step="exerciseIntensityConfig.inputConfig.step"
            hide-button
          >
            <template #suffix>
              <Select
                :model-value="modelValue.intensityCount[modelValue.intensityTypeName].unit"
                class="unit-type"
                :placeholder="$t('R4015')"
                :disabled="readonly"
                :options="intensityUnitOptions"
                :trigger-props="universalTriggerProps4select"
                @change="changeIntensityWeightUnit"
              />
            </template>
          </InputNumber>
        </template>
        <!-- 强度值--ftp-->
        <template v-if="intensityTypeConfig.type === 'ftp'">
          <div class="inline-block grouped-input border border-dark-60 rounded-sm flex flex-row justify-start items-center">
            <Select
              :model-value="modelValue.intensityCount[modelValue.intensityTypeName].type"
              class="intensity-zone w-auto align-middle relative"
              :popper-class="['group-style-options']"
              :class="['intensity-zone-' + containerId]"
              :disabled="readonly"
              :trigger-props="universalTriggerProps4select"
              @change="changeZone"
            >
              <Option
                v-for="(item, index) in exerciseIntensityConfig.typeConfig.options"
                :key="index"
                :value="item.type"
              >
                {{ item.nameText }}
              </Option>
              <Option :value="0">{{ $t('S4238') }}</Option>
            </Select>
            <!-- 已登录的才显示对应的范围-->
            <template v-if="userStore.userId || isCustomZoneType">
              <!--  为完整的范围的时候才会显示百分比和具体值组件-->
              <template v-if="!isLastZone && !isFirstZone">
                <div class="flex flex-row justify-start items-center">
                  <InputNumberRange
                    @change="changeFtpRange"
                    :model-value="modelValue.intensityCount[modelValue.intensityTypeName].ratioRange"
                    :min="exerciseIntensityConfig.ratioRangeConfig.min"
                    :max="exerciseIntensityConfig.ratioRangeConfig.max"
                    :step="exerciseIntensityConfig.ratioRangeConfig.step"
                    :precision="exerciseIntensityConfig.ratioRangeConfig.precision"
                    :disabled="readonly"
                    :hide-button="true"
                  >
                    <template #suffix>
                      <span class="pr-10">%</span>
                    </template>
                  </InputNumberRange>
                </div>
              </template>
              <div
                v-else-if="isFirstZone"
                class="one-ratio px-10 text-text-3"
              >
                {{ $t('G3024') }}
                <span class="text-white px-5">{{ modelValue.intensityCount[modelValue.intensityTypeName].ratioRange[1] }}</span>
                %
              </div>
              <div
                v-else
                class="one-ratio px-10 text-text-3"
              >
                {{ $t('G3023') }}
                <span class="text-white px-5">{{ modelValue.intensityCount[modelValue.intensityTypeName].ratioRange[0] }}</span>
                %
              </div>
            </template>
          </div>
          <!-- 范围值提示 -->
          <div
            v-if="userStore.userId"
            class="flex flex-row justify-start text-text-3 pl-10"
          >
            <template v-if="!isLastZone && !isFirstZone">
              <span class="program-distance-input-label text-center">{{
                modelValue.intensityCount[modelValue.intensityTypeName].range[0]
              }}</span>
              <span class="count-symbol">-</span>
              <span class="program-distance-input-label text-center">{{
                modelValue.intensityCount[modelValue.intensityTypeName].range[1]
              }}</span>
            </template>
            <div
              v-else-if="isFirstZone"
              class="text-center"
            >
              <span> {{ $t('G3024') }}</span>
              <span>{{ modelValue.intensityCount[modelValue.intensityTypeName].range[1] }}</span>
            </div>
            <div
              v-else
              class="text-center"
            >
              <span> {{ $t('G3023') }}</span>
              <span>{{ modelValue.intensityCount[modelValue.intensityTypeName].range[0] }}</span>
            </div>
            <span class="text-text-3 ml-10">w</span>
          </div>
        </template>
        <!-- 强度值--心率区间-->
        <template v-if="intensityTypeConfig.type === 'heart'">
          <!--  心率百分比-->
          <template v-if="intensityTypeConfig.isIntensityPercent">
            <div class="inline-block grouped-input border border-dark-60 rounded-sm flex flex-row justify-start items-center">
              <Select
                :model-value="modelValue.intensityCount[modelValue.intensityTypeName].type"
                class="intensity-zone w-auto align-middle relative"
                :popper-class="['group-style-options']"
                :disabled="readonly"
                :trigger-props="universalTriggerProps4select"
                @change="changeZone"
              >
                <Option
                  v-for="(item, index) in exerciseIntensityConfig.typeConfig.options"
                  :key="index"
                  :value="item.type"
                >
                  {{ item.nameText }}
                </Option>
                <Option :value="0">{{ $t('S4238') }}</Option>
              </Select>
              <!-- 已登录的才显示对应的范围-->
              <template v-if="userStore.userId || isCustomZoneType">
                <!--  为完整的范围的时候才会显示百分比和具体值组件-->
                <template v-if="!isLastZone && !isFirstZone">
                  <div class="flex flex-row justify-start items-center">
                    <InputNumberRange
                      @change="changeHrRatioRange"
                      :model-value="modelValue.intensityCount[modelValue.intensityTypeName].ratioRange"
                      :min="exerciseIntensityConfig.ratioRangeConfig.min"
                      :max="exerciseIntensityConfig.ratioRangeConfig.max"
                      :step="exerciseIntensityConfig.ratioRangeConfig.step"
                      :precision="exerciseIntensityConfig.ratioRangeConfig.precision"
                      :disabled="readonly"
                      :hide-button="true"
                    >
                      <template #suffix>
                        <span class="pr-10">%</span>
                      </template>
                    </InputNumberRange>
                  </div>
                </template>
                <div
                  v-else-if="isFirstZone"
                  class="one-ratio px-10 text-text-3"
                >
                  {{ $t('G3024') }}
                  <span class="text-white px-5">{{ modelValue.intensityCount[modelValue.intensityTypeName].ratioRange[1] }}</span>
                  %
                </div>
                <div
                  v-else
                  class="one-ratio px-10 text-text-3"
                >
                  {{ $t('G3023') }}
                  <span class="text-white px-5">{{ modelValue.intensityCount[modelValue.intensityTypeName].ratioRange[0] }}</span>
                  %
                </div>
              </template>
            </div>
            <!-- 范围值提示 -->
            <div
              v-if="userStore.userId"
              class="flex flex-row justify-start text-text-3 pl-10"
            >
              <template v-if="!isLastZone && !isFirstZone">
                <span class="program-distance-input-label text-center">{{
                  modelValue.intensityCount[modelValue.intensityTypeName].range[0]
                }}</span>
                <span class="count-symbol">-</span>
                <span class="program-distance-input-label text-center">{{
                  modelValue.intensityCount[modelValue.intensityTypeName].range[1]
                }}</span>
              </template>
              <div
                v-else-if="isFirstZone"
                class="text-center"
              >
                <span> {{ $t('G3024') }}</span>
                <span>{{ modelValue.intensityCount[modelValue.intensityTypeName].range[1] }}</span>
              </div>
              <div
                v-else
                class="text-center"
              >
                <span> {{ $t('G3023') }}</span>
                <span>{{ modelValue.intensityCount[modelValue.intensityTypeName].range[0] }}</span>
              </div>
              <span class="text-text-3 ml-10">bpm</span>
            </div>
          </template>
          <!--  心率绝对值-->
          <template v-else>
            <div class="grouped-input flex flex-row rounded-sm justify-start items-center border border-dark-60">
              <InputNumberRange
                :model-value="modelValue.intensityCount[modelValue.intensityTypeName].range"
                :min="exerciseIntensityConfig.rangeConfig.min"
                :max="exerciseIntensityConfig.rangeConfig.max"
                :step="exerciseIntensityConfig.rangeConfig.step"
                :precision="exerciseIntensityConfig.rangeConfig.precision"
                :disabled="readonly"
                :hide-button="true"
                @change="changeHrRange"
              >
                <template #suffix>
                  <span class="pr-10 text-text-3">bpm</span>
                </template>
              </InputNumberRange>
            </div>
          </template>
        </template>
        <!-- 强度值--配速区间-->
        <template v-if="['pace', 'adjustedPace'].includes(intensityTypeConfig.type)">
          <!-- 配速百分比-->
          <div
            class="border border-dark-60 rounded-sm flex flex-row justify-start"
            v-if="intensityTypeConfig.isIntensityPercent"
          >
            <!--  区间-->
            <Select
              :model-value="modelValue.intensityCount[modelValue.intensityTypeName].type"
              @update:model-value="(val) => updateModelValueByPath({ path: `intensityCount.${modelValue.intensityTypeName}.type`, val })"
              class="intensity-zone w-auto relative"
              :type="'text'"
              :popper-class="['group-style-options']"
              :class="['intensity-zone-' + containerId]"
              :disabled="readonly"
              :trigger-props="universalTriggerProps4select"
              @change="changeZone"
            >
              <Option
                v-for="item in exerciseIntensityConfig.typeConfig.options"
                :key="item.type"
                :value="item.type"
              >
                {{ item.nameText }}
              </Option>
              <Option :value="0">{{ $t('S4238') }}</Option>
            </Select>
            <!-- 已登录的才显示对应的范围-->
            <template v-if="userStore.userId || isCustomZoneType">
              <!--  为完整的范围的时候才会显示百分比和具体值组件-->
              <template v-if="!isLastZone && !isFirstZone">
                <div class="progam-input-group flex flex-row justify-start items-center">
                  <InputNumberRange
                    :model-value="modelValue.intensityCount[modelValue.intensityTypeName].ratioRange"
                    hide-button
                    :disabled="readonly"
                    :min="exerciseIntensityConfig.ratioRangeConfig.min"
                    :max="exerciseIntensityConfig.ratioRangeConfig.max"
                    :step="exerciseIntensityConfig.ratioRangeConfig.step"
                    :precision="exerciseIntensityConfig.ratioRangeConfig.precision"
                    @change="changePaceRatioRange"
                  >
                    <template #suffix>
                      <span class="px-10">%</span>
                    </template>
                  </InputNumberRange>
                </div>
              </template>
              <div
                v-else-if="isFirstZone"
                class="flex flex-row justify-start items-center text-text-3 px-10"
              >
                <span>{{ $t('G3024') }}</span>
                <span class="text-white px-10">{{ modelValue.intensityCount[modelValue.intensityTypeName].ratioRange[1] }}</span>
                <span>%</span>
              </div>
              <div
                v-else
                class="flex flex-row justify-start items-center text-text-3 px-10"
              >
                <span>{{ $t('G3023') }}</span>
                <span class="px-10 text-white">{{ modelValue.intensityCount[modelValue.intensityTypeName].ratioRange[0] }}</span>
                <span>%</span>
              </div>
            </template>
          </div>
          <!--  配速绝对值-->
          <template v-else>
            <!-- 配速单位为s/100m时，使用数字范围组件-->
            <div class="grouped-input border border-dark-60 rounded-sm flex flex-row justify-start items-center">
              <template v-if="modelValue.intensityCount[modelValue.intensityTypeName].unit === 's/100m'">
                <InputNumberRange
                  :model-value="modelValue.intensityCount[modelValue.intensityTypeName].range"
                  :disabled="readonly"
                  :min="exerciseIntensityConfig.rangeConfig.min"
                  :max="exerciseIntensityConfig.rangeConfig.max"
                  :step="exerciseIntensityConfig.rangeConfig.step"
                  :precision="exerciseIntensityConfig.rangeConfig.precision"
                  :no-border="true"
                  hide-button
                  @change="changeSpeedRange"
                >
                </InputNumberRange>
              </template>
              <!-- 配速单位不是s/100m时，使用分秒范围组件-->
              <template v-else>
                <TimePicker
                  class="program-timerange-picker border-none no-focus rounded-sm"
                  type="time-range"
                  :disabled="readonly"
                  :default-value="modelValue.intensityCount[modelValue.intensityTypeName].range.map(s2ms)"
                  :format="paceFormat"
                  :allow-clear="false"
                  disableConfirm
                  @change="changePaceRange"
                >
                  <template #suffix-icon>
                    <span></span>
                  </template>
                </TimePicker>
              </template>
              <!-- 单位选择 -->
              <Select
                :model-value="modelValue.intensityCount[modelValue.intensityTypeName].unit"
                @update:model-value="(val) => updateModelValueByPath({ path: `intensityCount.${modelValue.intensityTypeName}.unit`, val })"
                class="unit-type select-pace-unit text-text-3 relative"
                :class="['select-pace-unit-' + containerId]"
                :type="'text'"
                :placeholder="$t('R4015')"
                :disabled="readonly"
                :options="intensityUnitOptions"
                :trigger-props="universalTriggerProps4select"
                @change="changeIntensityPaceUnit"
                @click.stop
              />
            </div>
          </template>
          <!-- 提示 -->
          <div
            v-if="userStore.userId && intensityTypeConfig.isIntensityPercent"
            class="text-text-3 flex flex-row justify-start items-center pl-10"
          >
            <template v-if="!isLastZone && !isFirstZone">
              <!-- 配速单位为s/100m时，使用数字范围组件-->
              <span class="program-distance-input-label text-center">{{
                s2ms(modelValue.intensityCount[modelValue.intensityTypeName].range[1])
              }}</span>
              <span class="count-symbol">-</span>
              <span class="program-distance-input-label text-center">{{
                s2ms(modelValue.intensityCount[modelValue.intensityTypeName].range[0])
              }}</span>
            </template>
            <template v-else-if="isFirstZone">
              <div
                v-if="modelValue.intensityCount[modelValue.intensityTypeName].range[1]"
                class="one-ratio"
              >
                <span>{{ $t('G3067') }} </span>
                <span>{{ s2ms(modelValue.intensityCount[modelValue.intensityTypeName].range[1]) }}</span>
              </div>
            </template>
            <template v-else>
              <div
                v-if="modelValue.intensityCount[modelValue.intensityTypeName].range[0]"
                class="one-ratio"
              >
                <span> {{ $t('G3066') }}</span>
                <span>{{ s2ms(modelValue.intensityCount[modelValue.intensityTypeName].range[0]) }}</span>
              </div>
            </template>
            <span class="unit-text ml-10">{{ exerciseIntensityConfig.rangeConfig.unit }}</span>
          </div>
        </template>
        <!-- 强度值--速度区间-->
        <template v-if="modelValue.intensityTypeName === 'speed'">
          <div class="grouped-input flex flex-row justify-start items-center border border-dark-60 rounded-sm">
            <InputNumberRange
              :model-value="modelValue.intensityCount[modelValue.intensityTypeName].range"
              :disabled="readonly"
              :precision="exerciseIntensityConfig.rangeConfig.precision"
              :min="exerciseIntensityConfig.rangeConfig.min"
              :max="exerciseIntensityConfig.rangeConfig.max"
              :step="exerciseIntensityConfig.rangeConfig.step"
              :no-border="true"
              @change="changeSpeedRange"
            ></InputNumberRange>
            <Select
              :default-value="modelValue.intensityCount[modelValue.intensityTypeName].unit"
              class="unit-type w-auto relative"
              :class="['unit-type-speed-' + containerId]"
              :placeholder="$t('R4015')"
              :bordered="false"
              :disabled="readonly"
              :options="intensityUnitOptions"
              :trigger-props="universalTriggerProps4select"
              @change="changeIntensitySpeedUnit"
            />
          </div>
        </template>
        <!-- 强度值--泳姿-->
        <template v-if="modelValue.intensityTypeName === 'swimmingStyle'">
          <Select
            :model-value="modelValue.intensityCount[modelValue.intensityTypeName].type"
            class="intensity-zone w-auto border-dark-60 relative"
            :class="['intensity-zone-' + containerId]"
            :disabled="readonly"
            :trigger-props="universalTriggerProps4select"
            @change="changeSwimmingStyle"
          >
            <template #label="{ data }">
              <span>
                {{
                  data?.value === 0
                    ? exerciseIntensityConfig.typeConfig.options.find((item) => item.type === data?.value)?.nameText
                    : data?.value
                      ? exerciseIntensityConfig.typeConfig.options.find((item) => item.type === data?.value)?.name2Text || data.label
                      : ''
                }}</span
              >
            </template>
            <Option
              v-for="item in exerciseIntensityConfig.typeConfig.options"
              :key="item.type"
              :value="item.type"
            >
              {{ item.nameText }}
            </Option>
          </Select>
        </template>
        <!-- 强度值--功率-->
        <template v-if="modelValue.intensityTypeName === 'power'">
          <div class="border border-dark-60 rounded-sm flex flex-row justify-start items-center">
            <!-- 骑行类型下功率不能下拉选择 -->
            <Select
              v-if="criticalPower && sportType !== 2"
              :model-value="modelValue.intensityCount[modelValue.intensityTypeName].type"
              @update:model-value="(val) => updateModelValueByPath({ path: `intensityCount.${modelValue.intensityTypeName}.type`, val })"
              class="intensity-zone no-focus"
              :disabled="readonly"
              :trigger-props="universalTriggerProps4select"
              @change="changeZone"
            >
              <Option
                v-for="(item, index) in exerciseIntensityConfig.typeConfig.options"
                :key="index"
                :value="item.type"
                :label="item.nameText"
              >
              </Option>
              <Option :value="0">{{ $t('S4238') }}</Option>
            </Select>
            <!-- 已登录的才显示对应的范围-->
            <template v-if="userStore.userId || isCustomZoneType">
              <template v-if="!isLastZone">
                <div class="grouped-input inline-block rounded-sm flex flex-row justify-start items-center">
                  <InputNumberRange
                    :model-value="modelValue.intensityCount[modelValue.intensityTypeName].range"
                    @change="changePowerRange"
                    hide-button
                    :disabled="readonly"
                    :min="exerciseIntensityConfig.rangeConfig.min"
                    :max="exerciseIntensityConfig.rangeConfig.max"
                    :step="exerciseIntensityConfig.rangeConfig.step"
                    :precision="exerciseIntensityConfig.rangeConfig.precision"
                    :no-border="true"
                  ></InputNumberRange>
                </div>
              </template>
              <template v-else>
                <div class="one-ratio flex flex-row justify-start items-center pr-10">
                  <span>></span>
                  <span>{{ modelValue.intensityCount[modelValue.intensityTypeName].range[0] }}</span>
                </div>
              </template>
            </template>
          </div>
        </template>
        <!-- 强度值-步频 -->
        <template v-if="modelValue.intensityTypeName === 'cadence'">
          <div class="grouped-input border border-dark-60 rounded-sm">
            <InputNumberRange
              :model-value="modelValue.intensityCount[modelValue.intensityTypeName].range"
              hide-button
              :disabled="readonly"
              :min="exerciseIntensityConfig.rangeConfig.min"
              :max="exerciseIntensityConfig.rangeConfig.max"
              :step="exerciseIntensityConfig.rangeConfig.step"
              :precision="exerciseIntensityConfig.rangeConfig.precision"
              @change="changeCadenceRange"
            ></InputNumberRange>
            <!-- @change="(val) => updateModelValueByPath({ path: `intensityCount.${modelValue.intensityTypeName}.range`, val })" -->
          </div>
        </template>
        <!-- 强度值-难度 -->
        <template v-if="modelValue.intensityTypeName === 'gradeSystem'">
          <div class="grouped-input border border-dark-60 rounded-sm">
            <Select
              :model-value="modelValue.intensityCount[modelValue.intensityTypeName].type"
              @update:model-value="changeGradeSystem"
              class="pl-10 intensity-count w-auto relative"
              :class="['intensity-count-' + containerId]"
              :options="gradeSystemOptions"
              :disabled="readonly"
              :trigger-props="universalTriggerProps4select"
            >
            </Select>
          </div>
        </template>
      </template>
      <!-- 组数-->
      <template v-if="isStrength && ['train'].includes(modelValue.exerciseTypeName)">
        <Select
          class="pl-10 w-auto relative"
          :disabled="readonly"
          :class="['sets-' + containerId]"
          :options="[{ value: 1, label: $t('H6013') }]"
          :model-value="1"
          :trigger-props="universalTriggerProps4select"
        />
        <InputNumber
          :model-value="modelValue.sets"
          @update:model-value="(val) => updateModelValueByPath({ path: 'sets', val })"
          class="sets-count w-150 border-dark-60 border hover:border-primary-500"
          mode="button"
          :disabled="readonly"
          :min="1"
          :max="99"
          :controls="true"
        />
        <!-- 休息类型-->
        <Select
          :model-value="modelValue.restTypeName"
          @update:model-value="(val) => updateModelValueByPath({ path: 'restTypeName', val })"
          class="pl-10 rest-type w-auto relative"
          :class="['rest-' + containerId]"
          :disabled="readonly"
          :options="restOptions"
          :trigger-props="universalTriggerProps4select"
        />
        <!-- 休息类型值（时间）-->
        <template v-if="modelValue.restTypeName === 'time'">
          <TimePicker
            :model-value="parseDuration2time(modelValue.restCount.time.value)"
            @update:model-value="(val) => updateModelValueByPath({ path: 'restCount.time.value', val: hhmmss2seconds(val) })"
            class="rest-count duration-picker item-with-suffix border border-dark-60"
            :disabled="readonly"
            :min="timeRestConfig.inputConfig.min"
            :max="timeRestConfig.inputConfig.max"
            :unit="'h:m:s'"
            :allow-clear="false"
            disableConfirm
          >
            <template #suffix-icon><span>h:m:s</span></template>
          </TimePicker>
        </template>
      </template>
      <!-- 难度系统-->
      <template v-if="[6, 7].includes(sportType) && modelValue.intensityCount[modelValue.intensityTypeName].type !== 0">
        <Cascader
          :model-value="modelValue.intensityCount[modelValue.intensityTypeName].hardLevelValue"
          @update:model-value="handleUpdateGradeLevel"
          :format-label="formatgradeLevel"
          :options="gradeLevelOptions"
          class="pl-10 w-auto relative"
          :disabled="readonly"
          :trigger-props="universalTriggerProps4select"
        >
        </Cascader>
        <span
          v-if="userStore.userId"
          class="text-text-3 ml-10"
          >{{ displayHardLevelValue }}</span
        >
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, onBeforeUnmount, ref, watch } from 'vue';
import { cloneDeep } from 'lodash';
import { Select, Option, InputNumber, TimePicker, Tooltip, Trigger, InputGroup, Input, Cascader } from '@arco-design/web-vue';
import Definition from '@client/config/definition';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { hhmmss2seconds, s2ms, parseSeconds } from '@client/helper/day';
import { paceSKmToOther } from '@client/helper/pace';
import { countRHr, countRHrRatio } from '@client/helper/hr';
import { weightGToOther, weightToG } from '@client/helper/weight';
import { speedToKmh, speedKmhToOther } from '@client/helper/speed';
import { userStore as useUserStore } from '@client/store/userStore';
import SelectStrengthExercise from './SelectStrengthExercise/index.vue';
import StrengthExerciseDetail from './StrengthExerciseDetail/index.vue';
import InputNumberRange from '@client/components/InputNumberRange/index.vue';
import InputNumberFixed from '@client/components/InputNumberFixed/index.vue';
import { toComponentExercises, getExerciseByTypeFromDefinition, changeIntensity } from '../utils.js';
import { trainingProgramStore as useTrainingProgramStore } from '@client/store/trainingProgramStore';
import { initStore as useAppStore } from '@client/store/initStore';
import { useI18n } from 'vue-i18n';
import { unit as mathjsUnit } from 'mathjs';
import * as corosSportConfig from '@coros/sport-config';
import { useSwimPoolLength } from '../hooks/useSwimPoolLength';

const { distanceOptions, poolLengthUnit, convertDistanceValue, customPoolLength, inited: initedSwimPoolLength } = useSwimPoolLength();
// 泳池长度变了，选项变化了，重置一下
watch(distanceOptions, (newVal) => {
  if (!initedSwimPoolLength.value || props.readonly) return;

  nextTick(() => {
    if (newVal.length > 0) {
      const currentVal = props.modelValue.targetCount.distance.value;
      const val = convertDistanceValue(currentVal);
      // console.log(`currentVal`, currentVal, val);
      if (val !== currentVal) {
        updateModelValueByPath({ path: 'targetCount.distance.value', val });
      }
    }
  });
});
watch(poolLengthUnit, (newVal) => {
  if (!initedSwimPoolLength.value || props.readonly) return;

  const unitName = corosSportConfig.basic.getTargetUnitNameByType(newVal);
  if (unitName) {
    // changeTargetDistanceUnit 内部对游泳做了特殊处理
    changeTargetDistanceUnit(unitName);
  }
});

const {
  getDisplayHardLevelValue,
  getGradeLevelOptions,
  hardLevelConvert,
  getGradingSytemOptions,
  isIndoorClimbOrBoulderingTrainingSportType
} = corosSportConfig.sport.climb;

const paceFormat = `mm'ss"`;
dayjs.extend(duration);
const { t, te } = useI18n();

const userStore = useUserStore();
const universalTriggerProps4select = {
  contentClass: 'coros-trigger',
  autoFitPopupMinWidth: true,
  updateAtScroll: true
};

const props = defineProps({
  readonly: {
    type: Boolean,
    default: false
  },
  containerId: {
    type: String,
    default: ''
  },
  from: {
    type: String,
    default: 'template',
    validator: (val) => ['template', 'plan', 'schedule'].includes(val)
  },
  sportConfig: {
    type: Object,
    default: () => ({})
  },
  sportType: {
    type: Number,
    default: 1
  },
  sportCategoryName: {
    type: String,
    default: ''
  },
  user: {
    type: Object,
    default: () => ({})
  },
  modelValue: {
    type: Object,
    default: () => ({})
  },
  fastIntensityTypeName: {
    type: String,
    default: ''
  },
  isStrength: {
    type: Boolean,
    default: false
  },
  criticalPower: {
    type: Number,
    default: 0
  },
  zoneData: {
    type: Object,
    default: () => ({})
  },
  exerciseTypeOptions: {
    type: Array,
    default: () => []
  },
  exerciseDefinition: {
    type: Array,
    default: () => []
  },
  teamId: {
    type: String,
    default: ''
  },
  // 顶级难度系统值
  globalGradeSystem: {
    type: [String, Number],
    default: ''
  },
  // 父级决定使用谁的难度系统
  climbConfig: {
    type: Object,
    default: () => ({})
  }
});

const parseDuration2time = (durationVal, unit = 'seconds', format = 'HH:mm:ss') => {
  if (Array.isArray(durationVal)) {
    return durationVal.map((val) => dayjs.duration(val, unit).format(format));
  }
  return dayjs.duration(durationVal, unit).format(format);
};

const emit = defineEmits(['update:modelValue', 'delete', 'add', 'copy', 'changeGradeSystem']);
const trainingProgramStore = useTrainingProgramStore();
const appStore = useAppStore();

const exerciseConfig = computed(() => props.sportConfig[props.modelValue.exerciseTypeName]);

const targetOptions = computed(() => {
  if (props.modelValue.exerciseTypeName) {
    return (
      exerciseConfig.value.target.options
        // 游泳移除 负荷选项，但是要支持回显旧数据
        .filter((item) => {
          if (props.sportType === 3) {
            // 分解动作，目标只支持距离
            if (props.modelValue.intensityCount[props.modelValue.intensityTypeName].type === 6) {
              return item.key === 'distance';
            }
            // 非训练类型，移除负荷选项
            return props.modelValue.exerciseTypeName === 'train' || item.key !== 'load';
          }
          return true;
        })
        .map((item) => {
          return {
            label: item.nameText,
            value: item.key
          };
        })
    );
  }

  return [];
});

const exerciseTargetConfig = computed(() => {
  const typeConfig = exerciseConfig.value.target.typeConfig[props.modelValue.targetTypeName];
  const targetCount = props.modelValue.targetCount[props.modelValue.targetTypeName];
  const unit = targetCount.unit;
  // console.log(`targetCount`, props.modelValue.targetCount, props.modelValue.targetTypeName);
  return typeConfig.unitConfig[unit];
});

const targetUnitOptions = computed(() => {
  const typeConfig = exerciseConfig.value.target.typeConfig[props.modelValue.targetTypeName];
  return Object.keys(typeConfig.unitConfig).map((key) => {
    return {
      key,
      nameText: key
    };
  });
});
const changeTargetDistanceUnit = (unit) => {
  const { targetTypeName } = props.modelValue;

  // 游泳、目标=距离时，不转换数值
  if (props.sportType === 3) {
    if (targetTypeName === 'distance') {
      const targetCount = props.modelValue.targetCount[targetTypeName];
      targetCount.unit = unit;
    }
    return;
  }

  // 切换距离单位
  const typeConfig = exerciseConfig.value.target.typeConfig[targetTypeName];
  const targetCount = props.modelValue.targetCount[targetTypeName];
  const { value, unit: oldUnit } = targetCount;
  const precision = typeConfig.unitConfig?.[unit]?.inputConfig?.precision || 0;
  // 赋予新的单位
  targetCount.unit = unit;
  const distance = mathjsUnit(value, oldUnit).toNumber(unit).toFixed(precision);
  let val = Number(distance);
  const max = exerciseTargetConfig.value.inputConfig.max;
  if (val > max) {
    val = max;
  }
  updateModelValueByPath([{ path: 'targetCount.distance.value', val }]);
};
// 切换强度重量单位后
const changeIntensityWeightUnit = (unit) => {
  const exercise = props.modelValue;
  const { intensityTypeName } = exercise;
  const intensityCount = exercise.intensityCount[intensityTypeName];
  const { value, unit: oldUnit } = intensityCount;
  const baseValue = weightToG(oldUnit, value, 6);
  const typeConfig = exerciseConfig.value.intensity.typeConfig[intensityTypeName];
  const precision = typeConfig.unitConfig?.[unit]?.inputConfig?.precision || 0;
  const newValue = weightGToOther(unit, baseValue, precision);
  // 赋予新的单位
  intensityCount.unit = unit;
  intensityCount.value = newValue;
};
const fastIntensityTypeOptions = computed(() => {
  const intensity = cloneDeep(exerciseConfig.value.intensity.options);

  const optionsFilter = (options) => {
    return options.filter((item) => {
      if (Array.isArray(item.children)) {
        item.children = optionsFilter(item.children);
        return item.children.length > 0;
      }

      return [props.fastIntensityTypeName, 'swimmingStyle', 'notSet'].includes(item.key);
    });
  };

  function* dfs(data) {
    for (const item of data) {
      if (item.children && item.children.length > 0) {
        yield* dfs(item.children);
      } else {
        yield item;
      }
    }
  }

  let result = intensity;
  if (props.fastIntensityTypeName !== 'custom') {
    result = optionsFilter(result);
    // result = result.filter((item) => {
    //   return [
    //     that.programVm.info.fastIntensityTypeName,
    //     'swimmingStyle',
    //     'notSet'
    //   ].includes(item.key)
    // })
  }

  return [...dfs(result)];
});

const intensityTypeConfig = computed(() => {
  // return this.sportExerciseConfigure.intensity.typeConfig
  return exerciseConfig.value.intensity.typeConfig[props.modelValue.intensityTypeName];
});

const exerciseIntensityConfig = computed(() => {
  const { intensityCount, intensityTypeName } = props.modelValue;
  const _intensityCount = intensityCount[intensityTypeName];
  const unit = _intensityCount.unit;
  const intensityTypeConfig = exerciseConfig.value.intensity.typeConfig[intensityTypeName];
  const config = intensityTypeConfig.unitConfig[unit];
  return config;
});

const intensityUnitOptions = computed(() => {
  return Object.keys(intensityTypeConfig.value.unitConfig).map((key) => {
    return {
      value: key,
      label: key
    };
  });
});

const restOptions = computed(() => {
  if (!['train'].includes(props.modelValue.exerciseTypeName)) {
    return [];
  }

  return exerciseConfig.value.rest.options.map((item) => {
    return {
      label: item.nameText,
      value: item.key
    };
  });
});

const timeRestConfig = computed(() => {
  const typeConfig = exerciseConfig.value.rest.typeConfig.time;
  const unit = typeConfig.defaultUnit;
  return (
    typeConfig.unitConfig[unit] || {
      inputConfig: {}
    }
  );
});

const isCustomZoneType = computed(() => {
  return props.modelValue.intensityCount[props.modelValue.intensityTypeName].type === 0;
});

const isLastZone = computed(() => {
  const options = exerciseIntensityConfig.value?.typeConfig?.options;
  if (!options) {
    return false;
  }
  return options[options.length - 1].type === props.modelValue.intensityCount[props.modelValue.intensityTypeName].type;
});
const isFirstZone = computed(() => {
  const options = exerciseIntensityConfig.value?.typeConfig?.options;
  if (!options) {
    return false;
  }
  return options[0].type === props.modelValue.intensityCount[props.modelValue.intensityTypeName].type;
});

const exerciseTypeColor = computed(() => {
  const exerciseTypeInfo = Definition.exerciseTypeOptions[props.modelValue.exerciseTypeName];
  return (exerciseTypeInfo && exerciseTypeInfo.color) || '';
});

const updateModelValueByPath = (src) => {
  if (src) {
    const newData = cloneDeep(props.modelValue);
    const normalizeSrc = () => (Array.isArray(src) ? src : [src]);
    const assignValByPath = (data, path, val) => {
      path.split('.').reduce((obj, field, index, arr) => {
        if (index === arr.length - 1) {
          obj[field] = val;
        } else {
          return obj[field];
        }
      }, data);
    };

    normalizeSrc().forEach((item) => {
      assignValByPath(newData, item.path, item.val);
    });

    emit('update:modelValue', newData);
  }
};
const changeIntensityPaceUnit = (unit) => {
  const { intensityTypeName, intensityCount: intensityCountType } = props.modelValue;
  const intensityCount = intensityCountType[intensityTypeName];
  const { type, ratioRange } = intensityCount;
  let { ltsp } = props.zoneData;
  intensityCount.unit = unit;
  // 没有选择类型，则数据需要手动转换
  if (['', '0', 0, false].includes(type)) {
    ltsp = paceSKmToOther(unit, ltsp, 6);

    let [ratio0, ratio1] = ratioRange;
    ratio0 = ratio0 === 0 ? 100 : ratio0;
    ratio1 = ratio1 === 0 ? 100 : ratio1;
    const value0 = Math.round((ltsp * 100) / ratio0);
    const value1 = Math.round((ltsp * 100) / ratio1);

    let range = [value1, value0];
    intensityCount.range = range;

    updateModelValueByPath([
      {
        path: `intensityCount.${intensityTypeName}.range`,
        val: range
      }
    ]);
  } else {
    changePaceZone(type);
  }
};
const changeSwimmingStyle = (val) => {
  // @update:model-value="(val) => updateModelValueByPath({ path: `intensityCount.${modelValue.intensityTypeName}.type`, val })"
  const args = [{ path: `intensityCount.${props.modelValue.intensityTypeName}.type`, val: val }];

  // 6 是分解动作，这个时候目标只能选距离
  if (val === 6) {
    // @update:model-value="(val) => updateModelValueByPath({ path: 'targetTypeName', val })"
    args.push({
      path: `targetTypeName`,
      val: 'distance'
    });
  }

  updateModelValueByPath(args);
};
const getIntensityCount = () => {
  const { intensityCount: intensity, intensityTypeName } = props.modelValue;
  const intensityCount = intensity[intensityTypeName];
  return intensityCount;
};
const getIntensityTypeConfig = () => {
  const { intensityTypeName } = props.modelValue;
  const intensityTypeConfig = exerciseConfig.value.intensity.typeConfig[intensityTypeName];
  return intensityTypeConfig;
};
// 修改配速绝对值
const changePaceRange = (val) => {
  const { intensityTypeName } = props.modelValue;
  const intensityCount = getIntensityCount();
  const { unit } = intensityCount;
  let { ltsp } = props.zoneData;
  ltsp = paceSKmToOther(unit, ltsp, 6);

  let [rangeText0, rangeText1] = val;
  props.modelValue.isIntensityPercent = false;

  const range0 = parseSeconds(rangeText0, `mm'ss"`);
  const range1 = parseSeconds(rangeText1, `mm'ss"`);
  const range = [range0, range1];
  const ratioRange = [Math.round((ltsp / range1) * 100), Math.round((ltsp / range0) * 100)];

  intensityCount.type = 0;
  intensityCount.range = range;
  intensityCount.ratioRange = ratioRange;
  updateModelValueByPath([
    { path: `intensityCount.${intensityTypeName}.range`, val: range },
    { path: `intensityCount.${intensityTypeName}.ratioRange`, val: ratioRange }
  ]);
};
const changeSpeedRange = (val) => {
  const { intensityTypeName } = props.modelValue;
  const intensityCount = getIntensityCount();
  const { unit } = intensityCount;
  let { ltsp } = props.zoneData;

  ltsp = paceSKmToOther(unit, ltsp, 6);
  const [range0, range1] = val;
  const ratioRange = [Math.round((ltsp / range1) * 100), Math.round((ltsp / range0) * 100)];
  updateModelValueByPath([
    { path: `intensityCount.${intensityTypeName}.range`, val },
    { path: `intensityCount.${intensityTypeName}.ratioRange`, val: ratioRange }
  ]);
};
const changeIntensitySpeedUnit = (unit) => {
  const { intensityTypeName, intensityCount: intensityCountType, exerciseTypeName } = props.modelValue;
  const intensityCount = intensityCountType[intensityTypeName];
  const { range, unit: oldUnit } = intensityCount;
  // 先根据旧的单位将数值统一转换为m/h为单位的数值
  const [range0, range1] = range;
  const baseRange0 = speedToKmh(oldUnit, range0, 6);
  const baseRange1 = speedToKmh(oldUnit, range1, 6);
  const typeConfig = props.sportConfig[exerciseTypeName].intensity.typeConfig[intensityTypeName];

  const precision = typeConfig.unitConfig?.[unit]?.rangeConfig?.precision || 0;
  const resultRange = [speedKmhToOther(unit, baseRange0, precision), speedKmhToOther(unit, baseRange1, precision)];
  // 赋予新的单位
  intensityCount.unit = unit;
  intensityCount.range = resultRange;
};
const changeStrengthExercise = (data) => {
  const newExercise = toComponentExercises(
    data,
    trainingProgramStore.exercises,
    props.sportConfig,
    props.criticalPower,
    props.zoneData,
    false
  );

  newExercise.originId = data.id;
  newExercise.intensityTypeName = props.modelValue.intensityTypeName;

  ['name', 'overview', 'equipment', 'muscle', 'muscleRelevance', 'part'].forEach((field) => {
    newExercise[field] = data[field];
  });

  emit('update:modelValue', newExercise);
};
const changePowerRange = (val) => {
  const { intensityTypeName } = props.modelValue;
  const { criticalPower } = props;
  const intensityCount = getIntensityCount();
  intensityCount.type = 0; // 自定义
  const data = [{ path: `intensityCount.${intensityTypeName}.range`, val }];

  if (criticalPower) {
    const [range0, range1] = intensityCount.range;
    const ratioRange = [Math.round((range0 / criticalPower) * 100), Math.round((range1 / criticalPower) * 100)];
    data.push({ path: `intensityCount.${intensityTypeName}.ratioRange`, val: ratioRange });
  }
  updateModelValueByPath(data);
};
const changeFtpRange = (val) => {
  const { intensityTypeName } = props.modelValue;

  const ftp = props.zoneData.ftp || 0;

  const intensityCount = getIntensityCount();
  intensityCount.type = 0; // 切换为自定义选项
  const data = [{ path: `intensityCount.${intensityTypeName}.range`, val }];

  const [ratio0, ratio1] = val;
  const [ratioRange0, ratioRange1] = intensityCount.ratioRange;

  // %ftp对应的绝对功率计算公式为=百分比*FTP值
  const range = [Math.round((ratio0 / 100) * ftp), Math.round((ratio1 / 100) * ftp)];
  intensityCount.range = range;
  // console.log(`🔔 new ragne`, range);
  data.push(
    { path: `intensityCount.${intensityTypeName}.range`, val: range },
    { path: `intensityCount.${intensityTypeName}.ratioRange`, val }
  );

  updateModelValueByPath(data);
};
// 修改心率比值
const changeHrRatioRange = (val) => {
  const { intensityTypeName } = props.modelValue;

  const intensityTypeConfig = getIntensityTypeConfig();
  const intensityCount = getIntensityCount();
  const { hrType } = intensityTypeConfig;
  const currentZoneData = props.zoneData;
  const userHr = currentZoneData[hrType] || 0;
  const isRHr = hrType === 'rhr';

  props.modelValue.isIntensityPercent = true;
  intensityCount.type = 0; // 切换为自定义选项

  const [ratio0, ratio1] = val;
  const { rhr, maxHr } = currentZoneData;

  let range;
  if (isRHr) {
    range = [countRHr(rhr, maxHr, ratio0), countRHr(rhr, maxHr, ratio1)];
  } else {
    range = [(userHr * ratio0) / 100, (userHr * ratio1) / 100];
  }
  const [_range0, _range1] = range;
  range = [Math.round(_range0), Math.round(_range1)];
  intensityCount.range = range;
  // 计算范围值
  updateModelValueByPath([
    { path: `intensityCount.${intensityTypeName}.range`, val: range },
    { path: `intensityCount.${intensityTypeName}.ratioRange`, val }
  ]);
};
// 修改心率绝对值
const changeHrRange = (val) => {
  const { intensityTypeName } = props.modelValue;
  const intensityTypeConfig = getIntensityTypeConfig();
  const intensityCount = getIntensityCount();

  const { hrType } = intensityTypeConfig;
  const currentZoneData = props.zoneData;
  const userHr = currentZoneData[hrType] || 0;
  const isRHr = hrType === 'rhr';

  props.modelValue.isIntensityPercent = false;
  intensityCount.type = 0; // 切换为自定义选项

  const [range0, range1] = val;
  const { rhr, maxHr } = currentZoneData;

  let ratioRange;

  if (isRHr) {
    ratioRange = [countRHrRatio(userHr, maxHr, range0), countRHrRatio(userHr, maxHr, range1)];
  } else {
    ratioRange = [(range0 / userHr) * 100, (range1 / userHr) * 100];
  }
  intensityCount.ratioRange = ratioRange;
  // 计算范围值
  updateModelValueByPath([
    { path: `intensityCount.${intensityTypeName}.range`, val },
    { path: `intensityCount.${intensityTypeName}.ratioRange`, val: ratioRange }
  ]);
};
// 修改配速比值
const changePaceRatioRange = (val) => {
  const { intensityTypeName } = props.modelValue;
  const currentZoneData = props.zoneData;
  const intensityCount = getIntensityCount();

  const { unit } = intensityCount;

  const ltsp = paceSKmToOther(unit, currentZoneData.ltsp || 0, 6);

  props.modelValue.isIntensityPercent = true;

  const [ratio0, ratio1] = val;

  intensityCount.type = 0; // 切换为自定义选项

  const [range0, range1] = [Math.round(ltsp / (ratio0 / 100)), Math.round(ltsp / (ratio1 / 100))];

  const range = [range0, range1];
  intensityCount.range = range;
  updateModelValueByPath([
    { path: `intensityCount.${intensityTypeName}.range`, val: range },
    { path: `intensityCount.${intensityTypeName}.ratioRange`, val }
  ]);
};
// 修改步频
const changeCadenceRange = (val) => {
  const { intensityTypeName } = props.modelValue;
  updateModelValueByPath([{ path: `intensityCount.${intensityTypeName}.range`, val }]);
};
const getZoneFromOptions = (options, zoneType) => {
  let range = [];
  let ratioRange = [];
  let fistZone = null;
  let zone = null;

  const findIt = (list) => {
    if (zone) {
      return;
    }

    list.forEach((item) => {
      if (item.children) {
        findIt(item.children);
        return;
      }

      if (!fistZone && item.type) {
        fistZone = item;
      }

      if (item.type === zoneType) {
        zone = item;
      }
    });
  };

  findIt(options);
  if (zoneType && zone) {
    range = [Math.round(zone.range[0]), Math.round(zone.range[1])];
    ratioRange = [Math.round(zone.ratioRange[0]), Math.round(zone.ratioRange[1])];
  } else {
    range = [Math.round(fistZone.range[0]), Math.round(fistZone.range[1])];
    ratioRange = [Math.round(fistZone.ratioRange[0]), Math.round(fistZone.ratioRange[1])];
  }

  return {
    range,
    ratioRange
  };
};

// 变更心率区间
const changeHrZone = (zoneType) => {
  const rangeInfo = getZoneFromOptions(exerciseIntensityConfig.value.typeConfig.options, zoneType);

  const intensityCount = getIntensityCount();
  const { intensityTypeName } = props.modelValue;
  const { range, ratioRange } = rangeInfo;
  intensityCount.range = range;
  intensityCount.ratioRange = ratioRange;

  updateModelValueByPath([
    {
      path: `intensityCount.${intensityTypeName}.range`,
      val: range
    },
    {
      path: `intensityCount.${intensityTypeName}.ratioRange`,
      val: ratioRange
    }
  ]);
};
// 变更配速区间
const changePaceZone = (zoneType) => {
  const rangeInfo = getZoneFromOptions(exerciseIntensityConfig.value.typeConfig.options, zoneType);
  const intensityCount = getIntensityCount();
  const { intensityTypeName } = props.modelValue;
  const { range, ratioRange } = rangeInfo;
  intensityCount.range = range;
  intensityCount.ratioRange = ratioRange;
  updateModelValueByPath([
    {
      path: `intensityCount.${intensityTypeName}.range`,
      val: range
    },
    {
      path: `intensityCount.${intensityTypeName}.ratioRange`,
      val: ratioRange
    }
  ]);
};
// 变更功率区间
const changePowerZone = (zoneType) => {
  const rangeInfo = getZoneFromOptions(exerciseIntensityConfig.value.typeConfig.options, zoneType);
  const intensityCount = getIntensityCount();
  const { intensityTypeName } = props.modelValue;
  const { range, ratioRange } = rangeInfo;
  intensityCount.range = range;
  intensityCount.ratioRange = ratioRange;
  updateModelValueByPath([
    {
      path: `intensityCount.${intensityTypeName}.range`,
      val: range
    },
    {
      path: `intensityCount.${intensityTypeName}.ratioRange`,
      val: ratioRange
    }
  ]);
};
// 变更ftp区间
const changeFtpZone = (zoneType) => {
  const rangeInfo = getZoneFromOptions(exerciseIntensityConfig.value.typeConfig.options, zoneType);
  const intensityCount = getIntensityCount();
  const { intensityTypeName } = props.modelValue;
  const { range, ratioRange } = rangeInfo;
  intensityCount.range = range;
  intensityCount.ratioRange = ratioRange;
  updateModelValueByPath([
    {
      path: `intensityCount.${intensityTypeName}.range`,
      val: range
    },
    {
      path: `intensityCount.${intensityTypeName}.ratioRange`,
      val: ratioRange
    }
  ]);
};

const changeZone = (zoneType) => {
  const { intensityTypeName } = props.modelValue;
  const intensityTypeConfig = getIntensityTypeConfig();
  switch (intensityTypeConfig.type) {
    case 'heart':
      // 过滤跟区间无关的
      if (!['rhr'].includes(intensityTypeName)) {
        changeHrZone(zoneType);
      }
      break;
    case 'pace':
      // 过滤跟区间无关的
      if (!['thresholdPace'].includes(intensityTypeName)) {
        changePaceZone(zoneType);
      }
      break;
    case 'adjustedPace':
      // 过滤跟区间无关的
      if (!['adjustedPace'].includes(intensityTypeName)) {
        changePaceZone(zoneType);
      }
      break;
    case 'power':
      changePowerZone(zoneType);
      break;
    case 'ftp':
      changeFtpZone(zoneType);
      break;
  }
  updateModelValueByPath([
    {
      path: `intensityCount.${props.modelValue.intensityTypeName}.type`,
      val: zoneType
    }
  ]);
};

const changeIntensityType = async (type) => {
  const intensityTypeConfig = getIntensityTypeConfig();
  const { defaultIntensityCustom, isIntensityPercent } = intensityTypeConfig;
  const data2update = [
    { path: 'intensityTypeName', val: type },
    { path: 'isIntensityPercent', val: isIntensityPercent || false }
  ];
  const hasDefaultCustom = Reflect.has(intensityTypeConfig, 'defaultIntensityCustom');
  if (hasDefaultCustom) {
    data2update.push({ path: `intensityCount.${type}.type`, val: defaultIntensityCustom || false });
  }
  if (hasDefaultCustom) {
    const intensityCount = getIntensityCount();
    intensityCount.type = defaultIntensityCustom;
    changeZone(defaultIntensityCustom);
  }
  updateModelValueByPath(data2update);
};

const activeStrengthExercise = computed(() => {
  const exerciseCategoryObj = appStore.exerciseCategoryObj;
  const keys = ['part', 'muscle', 'equipment'];
  let result = null;

  function setText(item) {
    const result = cloneDeep(item);

    // 将数组转化为对应的文字
    keys.forEach((key) => {
      if (result[key]) {
        result[`${key}Text`] = result[key]
          .map((item) => {
            return getI18nText(key, item);
          })
          .filter((item) => item !== '')
          .join(',');
      }
    });

    result.descText = te(`${result.name}_DESC`) ? t(`${result.name}_DESC`) : t(`${result.name}_desc`);
    return result;
  }

  function getI18nText(dataType, dataValue) {
    return exerciseCategoryObj?.[dataType]?.[dataValue]?.dataCode ? t(exerciseCategoryObj[dataType][dataValue].dataCode) : '';
  }

  for (let n = 0, length = trainingProgramStore.exercises.length; n < length; n++) {
    const item = trainingProgramStore.exercises[n];
    if (item.id === props.modelValue.originId) {
      result = setText(item);
      break;
    }
  }

  if (!result) {
    result = setText(props.modelValue.originExercise);
  }
  return result;
});
const changeExerciseType = (type) => {
  const exerciseTypeInfo = Definition.getExerciseTypeInfoByName(type);
  // 取默认模板数据
  let newExercise = toComponentExercises(
    getExerciseByTypeFromDefinition(props.exerciseDefinition, exerciseTypeInfo.value),
    trainingProgramStore.exercises,
    props.sportConfig,
    props.criticalPower,
    props.zoneData,
    false,
    {
      climbConfig: getUserClimbConf(),
      customPoolLength: customPoolLength.value,
      poolLengthUnit: poolLengthUnit.value,
      isChangingExerciseType: true
    }
  );
  // console.log(111, props.sportConfig);
  // 保留之前选中的强度类型（默认模板的数据的intensityTypeName 有问题）
  newExercise = changeIntensity(newExercise, props.modelValue.intensityTypeName, props.sportConfig);
  if (props.globalGradeSystem && props.globalGradeSystem !== 'custom') {
    // console.log('有props.globalGradeSystem使用之');
    newExercise.intensityCount.gradeSystem.type = props.globalGradeSystem;
  }
  emit('update:modelValue', newExercise);
};
const showStrengthDetailButton = computed(() => {
  const exercise = activeStrengthExercise.value;
  if (exercise) {
    if (![null, undefined, '', 0].includes(exercise.defaultOrder) || exercise.exerciseTypeName === 4) {
      return false;
    }

    return true;
  }

  return false;
});

const timerList = ref([]);
onBeforeUnmount(() => {
  if (timerList.value.length) {
    // console.log('清理所有异步');
    timerList.value.forEach((item) => clearTimeout(item));
  }
});

// 更改难度系统，要换算难度等级
function changeGradeSystem(val, { fromProps } = {}) {
  if (val === 'custom') return;
  const { type: oldVal, hardLevelValue } = props.modelValue.intensityCount.gradeSystem;
  if (oldVal !== val) {
    const [levelType, level] = hardLevelValue.split('-').map((str) => parseInt(str, 10));
    // levelType=1 是相对等级，不用处理
    if (levelType === 2) {
      // 这里要在 intensityCount.gradeSystem.type 更新后才处理，这里用 nextTick 不行
      timerList.value.push(
        setTimeout(() => {
          const convertedLevel = hardLevelConvert(oldVal, val, level);
          handleUpdateGradeLevel(`2-${convertedLevel}`);
        })
      );
    }
  }
  updateModelValueByPath({ path: 'intensityCount.gradeSystem.type', val });
  // 防止从 props 过来的还触发了导致循环
  !fromProps && emit('changeGradeSystem', val);
}
function handleUpdateGradeLevel(val) {
  updateModelValueByPath({ path: 'intensityCount.gradeSystem.hardLevelValue', val });
}
function formatgradeLevel(options) {
  return options[1].label;
}
const gradeSystemOptions = computed(() => {
  const val = props.globalGradeSystem;
  if (val && 'custom' !== val) {
    return getGradeSystemOptions(val);
  }
  return exerciseIntensityConfig.value.typeConfig.options;
});
function getGradeSystemOptions(val) {
  // 二级选项应该是全局选项+不设置
  const currentOptions = getGradingSytemOptions(props.sportType);
  const res = currentOptions.filter((i) => [val, 0].includes(i.value));
  // console.log('获取自定义的难度系统选项', val, res);
  return res;
}
// 全局难度系统改变了
watch(
  () => props.globalGradeSystem,
  (val, oldVal) => {
    if (isIndoorClimbOrBoulderingTrainingSportType(props.sportType)) {
      if (val !== oldVal && val && 'custom' !== val) {
        // console.log('globalGradeSystem变化了');
        changeGradeSystem(val, { fromProps: true });
      }
    }
  }
);
// // 进来就要执行一次
// onMounted(() => {
//   nextTick(() => {
//     if (isIndoorClimbOrBoulderingTrainingSportType(props.sportType)) {
//       const val = props.globalGradeSystem;
//       if (val && 'custom' !== val) {
//         // changeGradeSystem(val, { fromProps: true });
//         console.log('执行处理下拉菜单选项的');
//         // handleGradeSystemOptions(val);
//       }
//     }
//   });
// });

// hardLevelValue 是前端虚拟的字段，处理同一个下拉菜单对应了两个字段的值的问题
// 两个字段是：intensityValue 和 onsightGradeOffset
const gradeLevelOptions = computed(() => {
  const gradingSystem = props.modelValue.intensityCount.gradeSystem.type;
  return getGradeLevelOptions(props.sportType, gradingSystem);
});
const displayHardLevelValue = computed(() => {
  const { intensityCount, intensityTypeName } = props.modelValue;
  const climbConfig = getUserClimbConf();
  if (!climbConfig) return '';
  return getDisplayHardLevelValue({
    globalGradeSystem: props.globalGradeSystem,
    exerciseGradeSystem: intensityCount.gradeSystem.type,
    climbConfig,
    sportType: props.sportType,
    val: intensityCount[intensityTypeName].hardLevelValue
  });
});

function getUserClimbConf() {
  return props.climbConfig;
}
</script>

<style lang="scss">
@import '@client/styles/_mixins.scss';
.training-program-sub-item {
  background: #232b40;
  position: relative;
  @apply mb-16;
  @apply rounded;
  // overflow: hidden;

  &:last-child {
    margin-bottom: 0;
  }

  .training-program-sub-item__title {
    height: px2rem144(48);
  }
  .arco-input-number-mode-button {
    .arco-input-number-step-button {
      border: none;
    }
  }

  .input-number-range {
    @apply bg-transparent;
  }
  .exercise-operate-btns {
    display: none;
  }
  &:hover {
    .exercise-operate-btns {
      display: block;
    }
  }
  .program-pace-input-suffix {
    width: px2rem144(110);
  }
  .program-pace-input {
    width: px2rem144(70);
  }
  .program-timerange-picker {
    width: px2rem144(160);
    padding: 4px;
  }
  .last-zone-value > .arco-input {
    @apply text-white;
    -webkit-text-fill-color: #fff;
  }
  // .intensity-zone {
  //   width: px2rem144(120);
  // }
  .select-pace-unit {
    width: px2rem144(100);
  }
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: var(--exercise-color);
  }
  .program-distance-inputgroup,
  .progam-input-group {
    width: px2rem144(170);
  }
  .training-program-sub-item__content {
    padding: 12px 8px;
  }

  .item-with-suffix {
    width: px2rem144(144);

    &.arco-input-number {
      &.arco-input-focus {
        background: transparent;
      }
    }
  }

  .grouped-input {
    .arco-input-number {
      .arco-input {
        width: 44px;
        text-align: center;
      }

      .arco-input-suffix {
        @apply text-text-3;
      }
    }
  }

  .arco-picker {
    --color-text-2: #fff;
    .arco-picker-suffix {
      @apply pl-10;
      @apply border-l;
      @apply border-dark-60;
    }

    .arco-picker-suffix-icon {
      @apply text-text-3;
    }
  }

  .arco-picker-range {
    .arco-picker-input {
      flex-shrink: 0;
      width: 55px;
    }
  }

  .arco-select.arco-select-view-focus {
    border-color: transparent;
    color: #0af;

    .arco-select-view-suffix {
      color: inherit;
    }
  }

  .coros-strength-exercise-select {
    width: px2rem144(460);
    .arco-input {
      -webkit-text-fill-color: #fff;
    }
  }

  .line-placeholder {
    width: 1px;
    height: px2rem144(36);
    border-top: 4px solid #232b40;
    border-bottom: 4px solid #232b40;
    background: rgba(255, 255, 255, 0.1);
  }
}
</style>
